import React, { useState, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { Order, OrderItemRecord, MenuItem } from '../types';
import { formatEuro, calculateVatBreakdown } from '../utils/format';
import {
  X,
  Plus,
  Minus,
  Trash2,
  Check,
  Edit3,
  AlertCircle,
  Utensils,
  Save,
  MessageSquare,
  Sparkles
} from 'lucide-react';

interface EditOrderModalProps {
  order: Order | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EditOrderModal: React.FC<EditOrderModalProps> = ({
  order,
  isOpen,
  onClose
}) => {
  const { categories, updateOrder } = useApp();

  const [items, setItems] = useState<OrderItemRecord[]>([]);
  const [specialNotes, setSpecialNotes] = useState('');
  const [selectedMenuItemId, setSelectedMenuItemId] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (order) {
      setItems(JSON.parse(JSON.stringify(order.items || [])));
      setSpecialNotes(order.special_notes || '');
      setSelectedMenuItemId('');
    }
  }, [order, isOpen]);

  if (!isOpen || !order) return null;

  // Flatten available menu items for the "Add Dish" dropdown
  const allMenuItems: MenuItem[] = categories.flatMap(c => c.items || []);

  // Update item quantity
  const handleUpdateQuantity = (idx: number, delta: number) => {
    setItems(prev => {
      const updated = [...prev];
      const newQty = updated[idx].quantity + delta;
      if (newQty <= 0) {
        return updated.filter((_, i) => i !== idx);
      }
      updated[idx] = { ...updated[idx], quantity: newQty };
      return updated;
    });
  };

  // Remove item
  const handleRemoveItem = (idx: number) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  // Update special instructions on item
  const handleUpdateItemInstructions = (idx: number, text: string) => {
    setItems(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], special_instructions: text };
      return updated;
    });
  };

  // Add new item from menu
  const handleAddMenuItem = () => {
    if (!selectedMenuItemId) return;
    const found = allMenuItems.find(m => m.id === selectedMenuItemId);
    if (!found) return;

    const newItem: OrderItemRecord = {
      id: `oi_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`,
      order_id: order.id,
      item_id: found.id,
      name: found.name_fi,
      price: found.price,
      quantity: 1,
      selected_variants: [],
      special_instructions: '',
      vat_rate: found.vat_rate
    };

    setItems(prev => [...prev, newItem]);
    setSelectedMenuItemId('');
  };

  // Calculate new totals
  const totalAmount = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const vatBreakdown = calculateVatBreakdown(
    items.map(it => ({
      price: it.price,
      quantity: it.quantity,
      vat_rate: it.vat_rate || 14.0
    }))
  );

  // Save changes
  const handleSaveChanges = async () => {
    if (items.length === 0) {
      alert('Order must contain at least 1 item.');
      return;
    }

    setIsSaving(true);
    try {
      const updatedOrder: Order = {
        ...order,
        items,
        total_amount: totalAmount,
        vat_food_amount: vatBreakdown.foodVat,
        vat_beverage_amount: vatBreakdown.beverageVat,
        special_notes: specialNotes,
        change_status: 'approved'
      };

      await updateOrder(updatedOrder);
      onClose();
    } catch (e) {
      console.error(e);
      alert('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border-medium)',
          borderRadius: 'var(--radius-xl)',
          width: '100%',
          maxWidth: '650px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--shadow-xl)',
          overflow: 'hidden'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'var(--bg-card)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '38px',
                height: '38px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--accent-copper-light)',
                color: 'var(--accent-copper)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Edit3 size={20} />
            </div>
            <div>
              <h3 style={{ fontSize: '1.15rem', fontWeight: 800, margin: 0 }}>
                Edit Order {order.order_number} (Table #{order.table_number})
              </h3>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                {order.customer_name} • {order.customer_phone}
              </div>
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '6px'
            }}
          >
            <X size={22} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div
          style={{
            padding: '20px 22px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px'
          }}
        >
          {/* Customer Requested Change Banner */}
          {order.change_request_notes && (
            <div
              style={{
                padding: '12px 16px',
                borderRadius: 'var(--radius-md)',
                background: 'rgba(245, 158, 11, 0.12)',
                border: '1px solid var(--accent-amber)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px'
              }}
            >
              <AlertCircle size={18} color="var(--accent-amber)" style={{ marginTop: '2px', flexShrink: 0 }} />
              <div>
                <div style={{ fontWeight: 800, fontSize: '0.86rem', color: 'var(--accent-amber)' }}>
                  Customer Modification Request:
                </div>
                <div style={{ fontSize: '0.86rem', color: 'var(--text-primary)', marginTop: '2px' }}>
                  "{order.change_request_notes}"
                </div>
              </div>
            </div>
          )}

          {/* Current Order Items */}
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.92rem', marginBottom: '10px' }}>
              Order Items ({items.length})
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {items.map((item, idx) => (
                <div
                  key={item.id || idx}
                  style={{
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 'var(--radius-md)',
                    padding: '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>
                      {item.name}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontWeight: 800, color: 'var(--accent-copper)', fontSize: '0.95rem' }}>
                        {formatEuro(item.price * item.quantity)}
                      </span>

                      {/* Quantity Stepper */}
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: 'var(--bg-surface)',
                          borderRadius: 'var(--radius-sm)',
                          padding: '2px 4px',
                          border: '1px solid var(--border-subtle)'
                        }}
                      >
                        <button
                          type="button"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-primary)',
                            padding: '4px'
                          }}
                          onClick={() => handleUpdateQuantity(idx, -1)}
                        >
                          <Minus size={14} />
                        </button>
                        <span style={{ fontWeight: 800, fontSize: '0.88rem', minWidth: '18px', textAlign: 'center' }}>
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: 'var(--text-primary)',
                            padding: '4px'
                          }}
                          onClick={() => handleUpdateQuantity(idx, 1)}
                        >
                          <Plus size={14} />
                        </button>
                      </div>

                      {/* Delete Item Button */}
                      <button
                        type="button"
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--accent-crimson)',
                          padding: '4px'
                        }}
                        title="Remove item"
                        onClick={() => handleRemoveItem(idx)}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Special Instructions input */}
                  <input
                    type="text"
                    value={item.special_instructions || ''}
                    placeholder="E.g., No onions, extra crispy, gluten-free crust..."
                    style={{
                      width: '100%',
                      padding: '6px 10px',
                      fontSize: '0.8rem',
                      background: 'var(--bg-surface)',
                      border: '1px solid var(--border-subtle)',
                      borderRadius: 'var(--radius-sm)',
                      color: 'var(--text-primary)'
                    }}
                    onChange={e => handleUpdateItemInstructions(idx, e.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Add Dish from Menu Section */}
          <div
            style={{
              padding: '14px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-card)',
              border: '1px dashed var(--border-medium)',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px'
            }}
          >
            <div style={{ fontWeight: 800, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Utensils size={16} color="var(--accent-copper)" />
              <span>Add Extra Dish / Beverage</span>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <select
                value={selectedMenuItemId}
                onChange={e => setSelectedMenuItemId(e.target.value)}
                style={{
                  flex: 1,
                  padding: '8px 12px',
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-subtle)',
                  color: 'var(--text-primary)',
                  fontSize: '0.86rem'
                }}
              >
                <option value="">Select dish to add...</option>
                {categories.map(cat => (
                  <optgroup key={cat.id} label={`${cat.icon || '🍽️'} ${cat.name_fi} (${cat.name_en})`}>
                    {(cat.items || []).map(dish => (
                      <option key={dish.id} value={dish.id}>
                        {dish.name_fi} ({dish.name_en}) — {formatEuro(dish.price)}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>

              <button
                type="button"
                className="btn-secondary"
                disabled={!selectedMenuItemId}
                onClick={handleAddMenuItem}
                style={{ padding: '8px 16px', fontSize: '0.86rem', whiteSpace: 'nowrap' }}
              >
                <Plus size={16} />
                <span>Add Item</span>
              </button>
            </div>
          </div>

          {/* Special Kitchen Notes */}
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.88rem', marginBottom: '6px' }}>
              Special Kitchen Notes
            </div>
            <textarea
              rows={2}
              value={specialNotes}
              onChange={e => setSpecialNotes(e.target.value)}
              placeholder="Overall notes for kitchen..."
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)',
                fontSize: '0.86rem',
                resize: 'none'
              }}
            />
          </div>

          {/* Calculated Totals Summary */}
          <div
            style={{
              padding: '14px 18px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--bg-card)',
              border: '1px solid var(--border-subtle)',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Food ALV (14%): {formatEuro(vatBreakdown.foodVat)} • Bev ALV (25.5%): {formatEuro(vatBreakdown.beverageVat)}
              </div>
              <div style={{ fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '2px' }}>
                Updated Order Total:
              </div>
            </div>

            <div style={{ fontSize: '1.4rem', fontWeight: 900, color: 'var(--accent-copper)' }}>
              {formatEuro(totalAmount)}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div
          style={{
            padding: '16px 22px',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            background: 'var(--bg-card)'
          }}
        >
          <button type="button" className="btn-secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>

          <button
            type="button"
            className="btn-primary"
            onClick={handleSaveChanges}
            disabled={isSaving || items.length === 0}
            style={{ background: 'var(--accent-emerald)', padding: '10px 22px' }}
          >
            <Save size={16} />
            <span>{isSaving ? 'Saving Changes...' : 'Save & Approve Order'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
